
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';

const fetchAnalyticsData = async (dateRange: DateRange | undefined) => {
  console.log('Fetching analytics data for range:', dateRange);
  
  const fromISO = dateRange?.from?.toISOString();
  const toISO = dateRange?.to?.toISOString();

  // Fetch all games without limit by using pagination internally
  const fetchAllGames = async () => {
    let allGames: any[] = [];
    let from = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        .from('games')
        .select('id, title, play_count, fake_play_count, rating, category, created_at')
        .eq('hidden', false)
        .range(from, from + pageSize - 1)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (data && data.length > 0) {
        allGames = [...allGames, ...data];
        from += pageSize;
        hasMore = data.length === pageSize;
      } else {
        hasMore = false;
      }
    }

    return allGames;
  };

  // First get a sample of recent sessions to check if we have data
  const { data: recentSessions } = await supabase
    .from('game_sessions')
    .select('created_at')
    .order('created_at', { ascending: false })
    .limit(5);

  console.log('Recent sessions sample:', recentSessions);

  // Build sessions query with proper date filtering
  let sessionsQuery = supabase
    .from('game_sessions')
    .select('game_id, duration_seconds, device_type, country_code, created_at');

  // Apply date range filter if provided, otherwise get all sessions
  if (fromISO && toISO) {
    console.log('Applying date filter:', { from: fromISO, to: toISO });
    sessionsQuery = sessionsQuery
      .gte('created_at', fromISO)
      .lte('created_at', toISO);
  } else {
    console.log('No date range provided, fetching all sessions');
  }

  const [allGames, categoriesResult, sessionsResult] = await Promise.all([
    fetchAllGames(),
    supabase.from('categories').select('id, name, slug'),
    sessionsQuery,
  ]);

  const categories = categoriesResult.data || [];
  const sessions = sessionsResult.data || [];
  
  console.log('Raw allGames data:', allGames.length, 'games found');
  console.log('Raw session data for period:', sessions.length, 'sessions found');
  console.log('Date range applied:', { fromISO, toISO });
  
  // If no sessions found in date range, show a message but continue with calculations
  if (sessions.length === 0 && fromISO && toISO) {
    console.warn('No sessions found in the selected date range. This might indicate:');
    console.warn('1. No users played games during this period');
    console.warn('2. The date range is in the future');
    console.warn('3. Session tracking is not working properly');
  }
  
  // Cumulative (All-Time) Stats
  const totalDisplayPlays = allGames.reduce((sum, game) => sum + (game.fake_play_count || game.play_count || 0), 0);
  const avgRating = allGames.length > 0 
    ? allGames.reduce((sum, game) => sum + (Number(game.rating) || 0), 0) / allGames.length 
    : 0;

  const categoryStats = categories.map(cat => {
    const categoryGames = allGames.filter(game => game.category === cat.slug);
    const categorySessionsInPeriod = sessions.filter(session => {
      const game = allGames.find(g => g.id === session.game_id);
      return game && game.category === cat.slug;
    });
    
    return {
      name: cat.name,
      games: categoryGames.length,
      real_plays: categorySessionsInPeriod.length,
      display_plays: categoryGames.reduce((sum, game) => sum + (game.fake_play_count || game.play_count || 0), 0)
    };
  });

  // Period-Specific Stats from Sessions
  const totalRealPlaysInPeriod = sessions.length;
  const totalPlaytimeSeconds = sessions.reduce((sum, s) => sum + (s.duration_seconds || 0), 0);
  const totalPlaytimeHoursInPeriod = (totalPlaytimeSeconds / 3600).toFixed(1);

  // Group sessions by month for the chart
  const monthlyData = sessions.reduce((acc, session) => {
    if (!session.created_at) return acc;
    const month = format(new Date(session.created_at), 'MMM yyyy');
    if (!acc[month]) {
      acc[month] = { month, real_plays: 0 };
    }
    acc[month].real_plays += 1;
    return acc;
  }, {} as Record<string, { month: string; real_plays: number }>);

  const deviceStats = sessions.reduce((acc, s) => {
    const device = s.device_type || 'Unknown';
    acc[device] = (acc[device] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const deviceDistribution = Object.entries(deviceStats).map(([name, value]) => ({ name, value }));
  
  const countryStats = sessions.reduce((acc, s) => {
    const country = s.country_code || 'Unknown';
    if (country !== 'Unknown' && country !== null) {
        acc[country] = (acc[country] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);
  
  const topCountries = Object.entries(countryStats)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);
  
  console.log('Country stats:', countryStats);
  console.log('Top countries:', topCountries);
  
  const gamePlaytime = sessions.reduce((acc, s) => {
    if (s.game_id && s.duration_seconds) {
        acc[s.game_id] = (acc[s.game_id] || 0) + s.duration_seconds;
    }
    return acc;
  }, {} as Record<string, number>);

  const topGamesByPlaytime = Object.entries(gamePlaytime)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([gameId, totalSeconds]) => {
      const game = allGames.find(g => g.id === gameId);
      return {
        title: game?.title || 'Unknown Game',
        playtime: `${(totalSeconds / 3600).toFixed(1)} hrs`,
      };
    });

  const topGamesByPlays = Object.entries(
    sessions.reduce((acc, s) => {
      if (s.game_id) acc[s.game_id] = (acc[s.game_id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  ).sort(([, a], [, b]) => b - a)
   .slice(0, 10)
   .map(([gameId, plays]) => {
      const game = allGames.find(g => g.id === gameId);
      if (!game) return null;
      return {
        ...game,
        real_play_count: plays,
        display_play_count: game.fake_play_count || game.play_count || 0,
      };
   })
   .filter(g => g !== null);

  const result = {
    totalGames: allGames.length,
    totalCategories: categories.length,
    totalRealPlays: totalRealPlaysInPeriod, // Now period-specific
    totalDisplayPlays, // All-time
    avgRating: avgRating.toFixed(1), // All-time
    topGames: topGamesByPlays, // Period-specific
    categoryStats, // Now period-specific for real_plays
    monthlyData: Object.values(monthlyData).sort((a,b) => new Date(a.month).getTime() - new Date(b.month).getTime()), // Period-specific
    totalPlaytimeHours: totalPlaytimeHoursInPeriod, // Period-specific
    deviceDistribution, // Period-specific
    topCountries, // Period-specific
    topGamesByPlaytime, // Period-specific
    hasData: sessions.length > 0, // Add flag to indicate if we have session data
    dateRange: { from: fromISO, to: toISO }, // Include date range in result
  };
  
  console.log('Final analytics result:', result);
  return result;
};

export const useAnalyticsData = (dateRange: DateRange | undefined) => {
  return useQuery({
    queryKey: ['analytics', dateRange?.from?.toISOString(), dateRange?.to?.toISOString()],
    queryFn: () => fetchAnalyticsData(dateRange),
    enabled: !!dateRange?.from && !!dateRange?.to, // only run query if both dates are defined
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });
};
